import { ActivationResponse, ForcePasswordChangeFormData } from "@data-types";
import { SessionStorageProvider, localizationService } from "@state";
import { action, observable, runInAction } from "mobx";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { getCurrentCulture } from "@utils";
import { UserAuthStore } from "@state/shared";
import { LoaderStore } from "@state/stores/common";

const loadFailPath = `/${getCurrentCulture()}/app-update`;
// prettier-ignore
const ForcePasswordChangeService = lazy(loadFailPath, ()=>import("@services/membership/force-password-change/ForcePasswordChangeService"));

export class PasswordResetStore {
	userAuthStore: UserAuthStore;
	loaderStore: LoaderStore;

	@observable public hasForcePwdResetError: boolean = false;
	@observable public forcePwdResetData: ActivationResponse | null = null;

	constructor(userAuthStore: UserAuthStore) {
		this.userAuthStore = userAuthStore;
		this.loaderStore = new LoaderStore();
	}

	@action.bound
	public async onInitializePasswordReset(): Promise<void> {
		if (this.userAuthStore.accountActivation?.activationToken == null) {
			return;
		}

		try {
			this.loaderStore.suspend();

			const forcePwdResetData = await (
				await ForcePasswordChangeService
			).default.getForcePasswordChangeData(
				this.userAuthStore.accountActivation?.activationToken,
				this.userAuthStore.accountActivation.userName
			);

			runInAction(() => {
				this.forcePwdResetData = forcePwdResetData;
			});
		} catch (error) {
			console.error(error);

			runInAction(() => {
				this.hasForcePwdResetError = true;
			});
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	public async handlePwdForceResetSubmit(
		data: ForcePasswordChangeFormData
	): Promise<void> {
		if (this.userAuthStore.accountActivation == null) {
			return;
		}

		this.loaderStore.suspend();

		const accountActivationModel = {
			recoveryToken: this.userAuthStore.accountActivation.activationToken,
			agencyId: this.userAuthStore.accountActivation.agencyId,
			shopId: this.userAuthStore.accountActivation.shopId,
			password: data.password,
			confirmPassword: data.newPasswordConfirm,
			username: data.userCardNumber,
		};

		try {
			const response = await (
				await ForcePasswordChangeService
			).default.createForcePasswordChange(accountActivationModel);

			await this.userAuthStore.loginStore.resolveUserActivationLogin(
				response.primary,
				response.secondary,
				Number(response.errorCode)
			);
		} catch (error) {
			if (error?.userData) {
				if (error.userData?.activationToken) {
					//show popup with user data
					this.userAuthStore.userDataConfirmationStore.setUserDataToBeConfirmed(
						{
							...error.userData,
							isInternetUser: false,
						}
					);
					this.userAuthStore.accountActivationStore.setUserConfirmationData(
						error.userData
					);
					this.userAuthStore.loginStore.setUserDataConfirmationState(
						true
					);
				} else {
					//show message about shop
					this.userAuthStore.rootStore.notificationStore.error(
						localizationService.t(
							"USER.POPUPS.DATA_CONFIRMATION.USER_DATA_CONFIRMATION_REQUIRED",
							{ 0: error.userData.shopInfo }
						)
					);
				}
			} else {
				this.userAuthStore.rootStore.notificationStore.error(
					localizationService.t(
						`MEMBERSHIP.FORCE_PASSWORD_CHANGE.${error?.message}`
					)
				);
			}
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	public async onCancelPwdForceReset(): Promise<void> {
		try {
			this.loaderStore.suspend();

			this.userAuthStore.removeUserFromLocalStorage();
			SessionStorageProvider.remove("slip");
		} catch (error) {
			console.error(error);

			this.userAuthStore.rootStore.notificationStore.error(
				localizationService.t(
					"error-handling:GENERIC_ERROR"
				)
			);
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	public clearForcePwdError(): void {
		this.hasForcePwdResetError = false;
	}

	@action.bound
	public onDisposePwdReset(): void {
		this.forcePwdResetData = null;
		this.clearForcePwdError();
	}
}
